import { useState, useEffect, useRef } from "react"
import { Search, Mic, Send, Menu, Info, ChevronLeft } from "lucide-react"
import { useSelector } from "react-redux"
import { getLetters } from "./actions"
import { getNews } from "../congresses/actions"
import { TabId } from "../../../shared/utils/enums" // Adjust path as needed
import { FilterItem } from "../../../interfaces/api/congresses"
import { useNavigate } from "react-router-dom"
import { routes } from "../../../config/routes"

const disclaimerText = [
  "● This information is provided for educational purposes only and is not intended as medical advice.",
  "● The information provided is for general informational purposes only and does not constitute medical, legal, or professional advice.",
  "● While efforts are made to ensure accuracy, the information may not be complete, up-to-date, or applicable to specific situations."
].join("\n\n");

const DrugsDiseases = () => {
  const [activeTab, setActiveTab] = useState<"drugs" | "diseases">("drugs")
  const [searchQuery, setSearchQuery] = useState("")
  const [askQuery, setAskQuery] = useState("")
  const [showLetters, setShowLetters] = useState(true)
  const [items, setItems] = useState<any[]>([])
  const [isLoadingItems, setIsLoadingItems] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [selectedLetter, setSelectedLetter] = useState<FilterItem | null>(null)
  const [page, setPage] = useState<number>(1)
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAnimated, setDisclaimerAnimated] = useState(false);
  const disclaimerRef = useRef<HTMLDivElement>(null);

  // Ref for the main container
  const containerRef = useRef<HTMLDivElement>(null)

  // Get loading state and tabs from Redux
  const { isLoadingTabs, tabs } = useSelector((state: any) => ({
    isLoadingTabs: state.auth?.isLoadingTabs,
    tabs: state.auth?.tabs
  }))

  // Get letters based on active tab
  const tabId = activeTab === "drugs" ? TabId.DRUGS : TabId.DISEASES // Adjust TabId values as needed
  const letters = getLetters(tabId)

  // Hardcoded specialty_id for now
  const specialty_id = 1
  const pageSize = 20

  const navigate = useNavigate();

  // Reset to letters view when tab changes
  useEffect(() => {
    setShowLetters(true)
    setItems([])
    setSelectedLetter(null)
    setHasMore(true)
    setPage(1)
    setSearchQuery("") // Optional: clear search when switching tabs
  }, [activeTab])

  // Infinite scroll effect - now listening to container scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return
      const container = containerRef.current
      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight
      // Check if scrolled near bottom (within 200px threshold)
      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 200
      if (scrolledToBottom && hasMore && !isLoadingItems && !showLetters) {
        loadMore();
      }
    }
    const container = containerRef.current
    if (container) {
      // Add scroll event listener to container
      container.addEventListener('scroll', handleScroll)
      // Cleanup
      return () => {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [hasMore, isLoadingItems, selectedLetter, showLetters, page, items.length, searchQuery]) // Added searchQuery

  // Fetch items by letter
  const fetchItems = async (letterItem: FilterItem, pageNumber = 1) => {
    if (isLoadingItems) {
      return;
    }
    
    setIsLoadingItems(true)
    try {
      const filters = { letter_id: letterItem.id, specialty_id }
      const response = await getNews(tabId, pageSize, pageNumber, filters)
      
      if (response && response.content && response.content.data) {
        const newData = response.content.data
        
        if (pageNumber === 1) {
          // Initial load - replace items
          setItems(newData)
        } else {
          // Load more - append items
          setItems(prev => [...prev, ...newData]);
        }
        
        setHasMore(newData.length === pageSize)
      } else {
        if (pageNumber === 1) {
          setItems([])
        }
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error fetching items:', error)
      setHasMore(false)
    } finally {
      setIsLoadingItems(false)
    }
  }

  // Handle letter click
  const handleAlphabetClick = (letterValue: string) => {
    const letterItem = letters.find(l => l.value === letterValue)
    if (letterItem) {
      setSelectedLetter(letterItem)
      setShowLetters(false)
      setItems([])
      setHasMore(true)
      setPage(1)
      fetchItems(letterItem, 1)
    }
  }

  // Load more items (supports both search and letter modes)
  const loadMore = () => {
    if (isLoadingItems || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    if (searchQuery.trim() !== "") {
      fetchSearchItems(searchQuery, nextPage);
    } else if (selectedLetter) {
      fetchItems(selectedLetter, nextPage);
    }
  }

  // Back button handler
  const handleBack = () => {
    setShowLetters(true)
    setItems([])
    setSelectedLetter(null)
    setHasMore(true)
    setPage(1)
  }

  // Fetch items by search (keywords)
  const fetchSearchItems = async (query: string, pageNumber = 1) => {
    if (isLoadingItems) return;
    setIsLoadingItems(true);
    try {
      const filters = { specialty_id, keywords: query };
      const response = await getNews(tabId, pageSize, pageNumber, filters);
      if (response && response.content && response.content.data) {
        const newData = response.content.data;
        if (pageNumber === 1) {
          setItems(newData);
        } else {
          setItems(prev => [...prev, ...newData]);
        }
        setHasMore(newData.length === pageSize);
      } else {
        if (pageNumber === 1) setItems([]);
        setHasMore(false);
      }
    } catch (error) {
      setHasMore(false);
    } finally {
      setIsLoadingItems(false);
    }
  };

  // Watch searchQuery and trigger search
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setShowLetters(true);
      setItems([]);
      setHasMore(true);
      setPage(1);
      return;
    }
    setShowLetters(false);
    setSelectedLetter(null);
    setItems([]);
    setHasMore(true);
    setPage(1);
    fetchSearchItems(searchQuery, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeTab]);

  const handleAsk = () => {
    console.log(`Asking about ${activeTab}: ${askQuery}`)
    setAskQuery("")
  }

  // Handle tab change
  const handleTabChange = (tab: "drugs" | "diseases") => {
    setActiveTab(tab)
    // The useEffect will handle the reset
  }

  // Skeleton loading component
  const SkeletonLetters = () => (
    <div className="flex flex-wrap gap-2 justify-center">
      {Array.from({ length: 26 }).map((_, index) => (
        <div
          key={index}
          className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse"
        />
      ))}
    </div>
  )

  // Skeleton loading for items
  const SkeletonItems = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-black animate-pulse"
        >
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
      ))}
    </div>
  )

  // Check if we have results
  const hasResults = items.length > 0

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showDisclaimer) return;
    const handleClick = (e: MouseEvent) => {
      if (disclaimerRef.current && !disclaimerRef.current.contains(e.target as Node)) {
        setShowDisclaimer(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDisclaimer]);

  // Only play animation the first time
  useEffect(() => {
    if (showDisclaimer && !disclaimerAnimated) {
      setDisclaimerAnimated(true);
    }
  }, [showDisclaimer, disclaimerAnimated]);

  return (
    <div 
      ref={containerRef}
      className="h-screen overflow-scroll min-h-screen"
    >

      {/* Hero Section */}
      <div className="px-6 pt-[150px] pb-4 bg-[linear-gradient(2.94064rad,rgb(119,254,255),rgb(100,146,240),rgb(86,91,227),rgb(63,0,204))] dark:bg-[linear-gradient(2.94064rad,rgb(0,102,102),rgb(0,73,136),rgb(0,59,170),rgb(0,0,139))]">
        {/* Search Question */}
        <div className="text-center mb-12">
          <h2 className="text-white text-xl font-normal mb-12">What would you like to search?</h2>
        </div>

        {/* Tab Buttons */}
        <div className="relative max-w-md mx-auto mb-8">
          <div className="relative bg-transparent border-2 border-white rounded-full p-1">
            <div className="flex">
              <button
                onClick={() => handleTabChange("drugs")}
                className={`flex-1 py-1 px-8 font-medium transition-all rounded-full relative z-10 text-md ${
                  activeTab === "drugs"
                    ? "bg-[#27b9e0] text-white"
                    : "bg-transparent text-white hover:bg-white/10"
                }`}
              >
                Drugs
              </button>
              <button
                onClick={() => handleTabChange("diseases")}
                className={`flex-1 py-1 px-8 font-medium transition-all rounded-full relative z-10 text-md ${
                  activeTab === "diseases"
                    ? "bg-[#27b9e0] text-white"
                    : "bg-transparent text-white hover:bg-white/10"
                }`}
              >
                Diseases
              </button>
            </div>
          </div>
        </div>

        {/* Search Input */}
        <div className="max-w-lg mx-auto mb-16">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 w-5 h-4 text-white bg-transparent" />
            <input
              type="text"
              placeholder="Enter keyword (e.g.: Asthma)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-14 pr-6 py-3 rounded-full bg-white border-2 border-white text-white placeholder-white focus:ring-2 focus:ring-white focus:outline-none text-sm !bg-transparent border-solid !border-white"
            />
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="bg-gray-50 dark:bg-black rounded-t-3xl pt-8 pb-32  mt-[-4%]">
        {/* Alphabet Section or Items List */}
        <div className="px-6 mb-8">
          <div className={`transition-all duration-500 ease-in-out ${showLetters ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none absolute'}`}>
            <>
              <h3 className="text-gray-900 dark:text-gray-400 font-medium mb-6 text-base">
                Find {activeTab === "drugs" ? "Drugs" : "Diseases"} By Alphabet
              </h3>
              {/* Loading State */}
              {isLoadingTabs ? (
                <SkeletonLetters />
              ) : letters.length > 0 ? (
                /* Letters from Backend */
                <div className="flex flex-wrap gap-2 justify-center">
                  {letters.map((letterItem: FilterItem) => (
                    <button
                      key={letterItem.id || letterItem.value}
                      onClick={() => handleAlphabetClick(letterItem.value)}
                      className="w-8 h-8 p-0 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:bg-primary hover:text-white dark:bg-darkCardBackground dark:hover:bg-primary/10 dark:hover:text-white transition-colors font-medium bg-white flex items-center justify-center"
                    >
                      {letterItem.value}
                    </button>
                  ))}
                </div>
              ) : (
                /* No Data Message */
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    No filters available for {activeTab}
                  </p>
                </div>
              )}
            </>
          </div>

          <div className={`transition-all duration-500 ease-in-out ${!showLetters ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none absolute'}`}>
            <>
              {/* Sticky Back Button */}
              <div className="sticky top-0 bg-gray-50 dark:bg-black z-20 pb-4 border-b border-gray-200 dark:border-gray-700">
                <button 
                  onClick={handleBack} 
                  className="flex items-center bg-primary text-white px-4 py-2 rounded-full hover:bg-primary/90 transition-all duration-200"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" /> Back to Letters
                </button>
              </div>
              
              {/* Initial Loading */}
              {isLoadingItems && items.length === 0 && (
                <SkeletonItems />
              )}

              {/* Results grid */}
              {hasResults && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {items.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-darkCardBackground text-gray-900 dark:text-white hover:bg-primary hover:text-white dark:hover:bg-primary/10 dark:hover:text-white transition-colors cursor-pointer"
                      onClick={() => navigate(routes.medical.drugsAndDiseasesDetails(item.id?.toString?.() || String(item.id), activeTab))}
                    >
                      <div className="text-sm font-medium">
                        {item.label || item.title || item.name || `Item ${idx + 1}`}
                      </div>
                      {item.description && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                          {item.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Loading more indicator */}
              {isLoadingItems && items.length > 0 && (
                <div className="w-full flex justify-center py-12">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 border-t-2 border-b-2 border-primary rounded-full animate-spin mb-4"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Loading more items...
                    </p>
                  </div>
                </div>
              )}

              {/* End message */}
              {hasResults && !hasMore && !isLoadingItems && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  ✨ You've reached the end!
                </div>
              )}

              {/* No results state */}
              {!hasResults && !isLoadingItems && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No results found.
                </div>
              )}
            </>
          </div>
        </div>
      </div>

      {/* Disclaimer Button */}
      <div className="fixed bottom-8 right-6 z-50">
        <button
          className="bg-primary backdrop-blur-sm text-white hover:bg-primary/90 rounded-full px-6 py-3 font-medium transition-colors flex items-center"
          onClick={() => setShowDisclaimer((v) => !v)}
        >
          <span className="mr-2">Disclaimer</span>
          <Info className="w-4 h-4" />
        </button>
        {showDisclaimer && (
          <div
            ref={disclaimerRef}
            className={`absolute bottom-16 right-0 w-[340px] bg-white/50 backdrop-blur-sm dark:bg-darkCardBackground border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 text-sm text-gray-700 dark:text-gray-200 z-50 ${disclaimerAnimated ? 'animate-fade-in-up' : ''}`}
          >
            <div className="whitespace-pre-line">
              <span className="text-md align-top mr-1">•</span> This information is provided for educational purposes only and is not intended as medical advice.
              <br /><br />
              <span className="text-md align-top mr-1">•</span> The information provided is for general informational purposes only and does not constitute medical, legal, or professional advice.
              <br /><br />
              <span className="text-md align-top mr-1">•</span> While efforts are made to ensure accuracy, the information may not be complete, up-to-date, or applicable to specific situations.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DrugsDiseases